import { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Search, X, Loader2 } from "lucide-react";
import { normalizeString } from "@/utils/searchUtils";
import { supabase } from "@/integrations/supabase/client";
import { JobEvaluationFilters } from "@/types/avaliacao";
import type { Agencia } from "@/types/agencia";

interface AvaliacaoFiltersProps {
  currentFilters: JobEvaluationFilters;
  onFiltersChange: (filters: JobEvaluationFilters) => void;
}

export function AvaliacaoFilters({ currentFilters, onFiltersChange }: AvaliacaoFiltersProps) {
  // Valores derivados direto de currentFilters (componente controlado pelo pai):
  // filtros restaurados do localStorage depois do mount aparecem nos selects.
  const freelancer = currentFilters.freelancer_id || "todos";
  const agencia = currentFilters.agencia_id || "todas";
  const status = currentFilters.status || "todos";
  const evaluatorType = currentFilters.evaluator_type || "todos";
  const [searchFreelancer, setSearchFreelancer] = useState("");
  const [searchAgencia, setSearchAgencia] = useState("");

  // PERF: busca server-side (limit 50) em vez de carregar a tabela inteira de
  // ~5,5k freelancers só para popular um dropdown de filtro. A RPC ignora acento
  // e caixa ("joao" acha "João") e inclui inativos, que também têm avaliações.
  const { data: freelancers = [], isLoading: isLoadingFreelancers } = useQuery({
    queryKey: ["freelancer-filter-options", searchFreelancer.trim()],
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async () => {
      // RPC fora dos tipos gerados do Supabase (migração 108/127).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("autocomplete_freelancers", {
        p_search: searchFreelancer.trim() || null,
        p_limit: 50,
        p_only_active: false,
      });
      if (error) throw error;
      return (data ?? []) as { id: string; nome: string; is_active: boolean }[];
    },
  });

  // Query para carregar agências — key dedicada de opções (id+nome), isolada
  // do cache de shape completo em ["agencias"].
  const { data: allAgencias = [], isLoading: isLoadingAgencias } = useQuery({
    queryKey: ["agencias", "options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome")
        .order("nome");

      if (error) throw error;
      return (data || []) as Agencia[];
    },
    staleTime: 1000 * 60 * 15, // 15 minutos
  });

  // Filtrar agências em memória conforme o termo de busca
  const agencias = useMemo(() => {
    if (!allAgencias || allAgencias.length === 0) return [];

    if (!searchAgencia.trim()) {
      return allAgencias
        .map((agencia) => ({ id: agencia.id, nome: agencia.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
    }

    const normalizedSearch = normalizeString(searchAgencia);

    return allAgencias
      .filter((agencia) => normalizeString(agencia.nome).includes(normalizedSearch))
      .map((agencia) => ({ id: agencia.id, nome: agencia.nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
  }, [allAgencias, searchAgencia]);

  // Garante que o freelancer selecionado (ex.: filtro restaurado do
  // localStorage) apareça no dropdown mesmo fora do top-50 da busca.
  const { data: freelancerSelecionado } = useQuery({
    queryKey: ["freelancer-filter-selected", freelancer],
    enabled: freelancer !== "todos",
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("freelancers")
        .select("id, nome, is_active")
        .eq("id", freelancer)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const opcoesFreelancers = useMemo(() => {
    if (!freelancerSelecionado) return freelancers;
    if (freelancers.some((opcao) => opcao.id === freelancerSelecionado.id)) return freelancers;
    return [freelancerSelecionado, ...freelancers];
  }, [freelancers, freelancerSelecionado]);

  // Filtro LIVE: emite a cada mudança (sem botão "Aplicar"). `next` sobrepõe o
  // valor recém-alterado sobre os valores atuais vindos de currentFilters.
  const emitir = (
    next: Partial<{ freelancer: string; agencia: string; status: string; evaluatorType: string }>,
  ) => {
    const merged = { freelancer, agencia, status, evaluatorType, ...next };
    onFiltersChange({
      freelancer_id: merged.freelancer === "todos" ? undefined : merged.freelancer,
      agencia_id: merged.agencia === "todas" ? undefined : merged.agencia,
      status: merged.status === "todos" ? undefined : (merged.status as JobEvaluationFilters["status"]),
      evaluator_type:
        merged.evaluatorType === "todos"
          ? undefined
          : (merged.evaluatorType as JobEvaluationFilters["evaluator_type"]),
    });
  };

  const limparFiltros = () => {
    setSearchFreelancer("");
    setSearchAgencia("");
    onFiltersChange({});
  };

  const temFiltrosAtivos =
    freelancer !== "todos" || agencia !== "todas" || status !== "todos" || evaluatorType !== "todos";

  return (
    <FilterToolbar className="items-end">
      {/* Freelancer */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <Label className="text-xs text-muted-foreground">Freelancer</Label>
        <Select
          value={freelancer}
          onValueChange={(value) => emitir({ freelancer: value })}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <div className="sticky top-0 p-2 bg-background border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar freelancer..."
                  value={searchFreelancer}
                  onChange={(e) => setSearchFreelancer(e.target.value)}
                  // Evita o crash de focus do typeahead nativo do Radix Select
                  // (item filtrado/desmontado). Só bloqueia caracteres imprimíveis.
                  onKeyDown={(e) => {
                    if (e.key.length === 1) e.stopPropagation();
                  }}
                  className="pl-8 h-7 text-xs"
                />
              </div>
            </div>
            <ScrollArea className="h-[200px]">
              <div className="p-2">
                <SelectItem value="todos">Todos os freelancers</SelectItem>
                {isLoadingFreelancers ? (
                  <div className="flex flex-col items-center justify-center p-4 space-y-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <p className="text-xs text-muted-foreground">Carregando freelancers...</p>
                  </div>
                ) : opcoesFreelancers.length > 0 ? (
                  opcoesFreelancers.map((opcaoFreelancer) => (
                    <SelectItem
                      key={opcaoFreelancer.id}
                      value={opcaoFreelancer.id}
                      className={opcaoFreelancer.is_active ? "" : "text-muted-foreground"}
                    >
                      {opcaoFreelancer.nome}{" "}
                      {!opcaoFreelancer.is_active && (
                        <span className="text-red-500 ml-2">inativo</span>
                      )}
                    </SelectItem>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-xs">
                    {searchFreelancer
                      ? `Nenhum freelancer encontrado para "${searchFreelancer}"`
                      : "Nenhum freelancer encontrado"}
                  </div>
                )}
              </div>
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>

      {/* Agência */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <Label className="text-xs text-muted-foreground">Agência</Label>
        <Select
          value={agencia}
          onValueChange={(value) => emitir({ agencia: value })}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <div className="sticky top-0 p-2 bg-background border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar agência..."
                  value={searchAgencia}
                  onChange={(e) => setSearchAgencia(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key.length === 1) e.stopPropagation();
                  }}
                  className="pl-8 h-7 text-xs"
                />
              </div>
            </div>
            <ScrollArea className="h-[200px]">
              <div className="p-2">
                <SelectItem value="todas">Todas as agências</SelectItem>
                {isLoadingAgencias ? (
                  <div className="flex flex-col items-center justify-center p-4 space-y-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <p className="text-xs text-muted-foreground">Carregando agências...</p>
                  </div>
                ) : agencias.length > 0 ? (
                  agencias.map((opcaoAgencia) => (
                    <SelectItem key={opcaoAgencia.id} value={opcaoAgencia.id}>
                      {opcaoAgencia.nome}
                    </SelectItem>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-xs">
                    {!allAgencias || allAgencias.length === 0
                      ? "Carregando agências..."
                      : searchAgencia
                        ? `Nenhuma agência encontrada para "${searchAgencia}"`
                        : "Nenhuma agência encontrada"}
                  </div>
                )}
              </div>
            </ScrollArea>
          </SelectContent>
        </Select>
      </div>

      {/* Tipo de Avaliador */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <Label className="text-xs text-muted-foreground">Tipo de avaliador</Label>
        <Select
          value={evaluatorType}
          onValueChange={(value) => emitir({ evaluatorType: value })}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            <SelectItem value="agency">Agência</SelectItem>
            <SelectItem value="freelancer">Freelancer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status de Preenchimento */}
      <div className="flex w-full flex-col gap-1 sm:w-auto">
        <Label className="text-xs text-muted-foreground">Status de preenchimento</Label>
        <Select
          value={status}
          onValueChange={(value) => emitir({ status: value })}
        >
          <SelectTrigger className="w-full sm:w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            <SelectItem value="preenchido">Preenchido</SelectItem>
            <SelectItem value="nao_preenchido">Não preenchido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {temFiltrosAtivos && (
        <Button
          variant="ghost"
          size="sm"
          onClick={limparFiltros}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="mr-1 h-3 w-3" />
          Limpar
        </Button>
      )}
    </FilterToolbar>
  );
}
